import { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import { socket } from "../services/socket";
import SpecCard from "../components/SpecCard";
import SpecRow from "../components/SpecRow";
import SpecSummaryCard from "../components/SpecSummaryCard";

import {
    FaBolt,
    FaBatteryHalf,
    FaSolarPanel,
    FaMicrochip,
    FaCog
} from "react-icons/fa";

import "../styles/specs.css";

import { Link } from "react-router-dom";

import "../styles/specs.css";

const ACTIVE_KEY = "activeDeviceSerial";

export default function InverterSpecs() {

    const [activeSerial] = useState(
        () => localStorage.getItem(ACTIVE_KEY) || ""
    );

    const [rating, setRating] = useState(null);
    const [deviceStatus, setDeviceStatus] = useState("Offline");

    // Drawer state
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {

        if (!activeSerial) return;

        // Switching devices should show a blank slate, not the
        // previous device's rating, while the new one loads.
        setRating(null);
        setDeviceStatus("Offline");

        socket.emit("request:rating", activeSerial);
        socket.emit("get-rating", activeSerial);

        function onRating(msg) {

            if (msg.serial === activeSerial) {
                setRating(msg.data);
            }

        }

        function onStatus(msg) {

            if (msg.serial === activeSerial) {
                setDeviceStatus(msg.status);
            }

        }

        socket.on("device:rating", onRating);
        socket.on("device:status", onStatus);

        return () => {
            socket.off("device:rating", onRating);
            socket.off("device:status", onStatus);
        };

    }, [activeSerial]);

    if (!activeSerial) {

        return (

            <div className="layout">

                <Sidebar menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

                <div className="content">

                    <Topbar setMenuOpen={setMenuOpen} title="Inverter Specs" subtitle="No device connected" />

                    <div className="dashboardEmptyState">
                        <FaMicrochip />
                        <h2>No device connected yet</h2>
                        <p>Add your inverter's Device ID to see its specifications here.</p>
                        <Link to="/devices" className="dashboardEmptyBtn">
                            Add Device
                        </Link>
                    </div>

                </div>

            </div>

        );

    }

    return (

        <div className="layout">

            <Sidebar
                menuOpen={menuOpen}
                setMenuOpen={setMenuOpen}
            />

            <div className="content">

                <Topbar
                    deviceStatus={deviceStatus}
                    setMenuOpen={setMenuOpen}
                    serial={activeSerial}
                />

                <div className="specPageHeader">

                    <div className="summaryGrid">

                        <SpecSummaryCard
                            title="Rated Output"
                            value={`${rating?.out_v_rating || "--"} V`}
                            subtitle={`${rating?.out_i_rating || "--"} A`}
                            color="#4FC3F7"
                            icon={<FaBolt />}
                        />

                        <SpecSummaryCard
                            title="Battery"
                            value={rating?.bat_type || "--"}
                            subtitle={`${rating?.bat_bulk_v || "--"} V Bulk`}
                            color="#FFD54F"
                            icon={<FaBatteryHalf />}
                        />

                        <SpecSummaryCard
                            title="Charging"
                            value={(rating?.charger_priority || "--").replaceAll("_", " ")}
                            subtitle={(rating?.out_priority || "--").replaceAll("_", " ")}
                            color="#66BB6A"
                            icon={<FaSolarPanel />}
                        />

                        <SpecSummaryCard
                            title="Machine"
                            value={(rating?.machine_type || "--").replaceAll("_", " ")}
                            subtitle={(rating?.output_mode || "--").replaceAll("_", " ")}
                            color="#FF8A65"
                            icon={<FaCog />}
                        />

                    </div>

                    <div>

                        <h1>Inverter Specifications</h1>

                        <p>{activeSerial} • Live Device Rating</p>

                    </div>

                </div>

                <div className="specsContainer">

                    <SpecCard
                        title="AC Ratings"
                        icon={<FaBolt color="#4FC3F7" />}
                    >

                        <SpecRow
                            label="Grid Voltage"
                            value={rating?.grid_v_rating}
                            unit="V"
                        />

                        <SpecRow
                            label="Grid Current"
                            value={rating?.grid_i_rating}
                            unit="A"
                        />

                        <SpecRow
                            label="Output Voltage"
                            value={rating?.out_v_rating}
                            unit="V"
                        />

                        <SpecRow
                            label="Output Frequency"
                            value={rating?.out_f_rating}
                            unit="Hz"
                        />

                        <SpecRow
                            label="Output Current"
                            value={rating?.out_i_rating}
                            unit="A"
                        />

                    </SpecCard>

                    <SpecCard
                        title="Battery"
                        icon={<FaBatteryHalf color="#FFD54F" />}
                    >

                        <SpecRow
                            label="Battery Type"
                            value={rating?.bat_type}
                        />

                        <SpecRow
                            label="Bulk Voltage"
                            value={rating?.bat_bulk_v}
                            unit="V"
                        />

                        <SpecRow
                            label="Float Voltage"
                            value={rating?.bat_float_v}
                            unit="V"
                        />

                        <SpecRow
                            label="Max Charge Current"
                            value={rating?.max_charge_i}
                            unit="A"
                        />

                        <SpecRow
                            label="Max AC Charge"
                            value={rating?.max_ac_charge_i}
                            unit="A"
                        />

                    </SpecCard>

                    <SpecCard
                        title="System"
                        icon={<FaMicrochip color="#66BB6A" />}
                    >

                        <SpecRow
                            label="Machine Type"
                            value={rating?.machine_type}
                        />

                        <SpecRow
                            label="Topology"
                            value={rating?.topology}
                        />

                        <SpecRow
                            label="Output Mode"
                            value={rating?.output_mode}
                        />

                        <SpecRow
                            label="Output Priority"
                            value={rating?.out_priority}
                        />

                        <SpecRow
                            label="Charger Priority"
                            value={rating?.charger_priority}
                        />

                        <SpecRow
                            label="Firmware Version"
                            value={rating?.fw_version}
                        />

                    </SpecCard>

                </div>

            </div>

        </div>

    );

}